import config from '../config';

const getAuthBasePath = () => (process.env.BASEURL ? `/${process.env.BASEURL}` : '');

const getRootRedirectTargets = () => {
  const basePath = getAuthBasePath();
  return new Set(
    ['/', '/index.html', basePath, `${basePath}/`, `${basePath}/index.html`].filter((path): path is string =>
      Boolean(path),
    ),
  );
};

export const normalizeAuthRedirectPath = (path: string): string => {
  const parsed = new URL(path, window.location.origin);
  if (getRootRedirectTargets().has(parsed.pathname)) {
    return `${getAuthBasePath()}/accounts${parsed.search}`;
  }

  return `${parsed.pathname}${parsed.search}`;
};

export const getCurrentAuthRedirectPath = (): string =>
  normalizeAuthRedirectPath(`${window.location.pathname}${window.location.search}`);

export const setAuthRedirectPath = (): void => {
  sessionStorage.setItem('oauthRedirectPath', getCurrentAuthRedirectPath());
};

/**
 * Redirects the user to the Okta login page.
 * Centralized login redirect function used by both auth module and error handlers.
 */
export const redirectToLogin = (): void => {
  window.location.assign(`${config.gonfalon.host}/private/login?dest=${encodeURI(window.location.href)}`);
};

// Use a timestamped approach to prevent multiple simultaneous auth redirects
let authRedirectTime = 0;
const AUTH_REDIRECT_TIMEOUT = 5000;

/**
 * Handles 401 authentication errors by redirecting to the login page.
 * Includes debouncing to prevent multiple simultaneous redirects.
 *
 * @param status - HTTP status code to check
 */
export const handleAuthRedirect = (status: number | undefined): void => {
  // Check for 401 Unauthorized and redirect to login
  if (status === 401) {
    const currentTime = Date.now();
    if (currentTime - authRedirectTime < AUTH_REDIRECT_TIMEOUT) {
      // oxlint-disable-next-line no-console -- Important for debugging auth redirects
      console.log('Auth redirect already in progress, ignoring additional 401 error');
      return;
    }
    authRedirectTime = currentTime;
    // oxlint-disable-next-line no-console -- Important for debugging auth redirects
    console.warn('Authentication token expired, redirecting to login...');
    setAuthRedirectPath();
    redirectToLogin();
  }
};
